
import React, { Component } from "react";
import styled from "styled-components";

const Page = styled.section`
  direction: rtl;
  position: relative;
  overflow: hidden;
  padding: 110px 20px 130px;

  background:
    radial-gradient(circle at 90% 5%, rgba(37, 99, 235, 0.22), transparent 30%),
    radial-gradient(circle at 5% 70%, rgba(124, 58, 237, 0.16), transparent 30%),
    linear-gradient(145deg, #050b16 0%, #081525 48%, #0b1d35 100%);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
    background-size: 55px 55px;
    pointer-events: none;
  }

  @media (max-width: 600px) {
    padding: 85px 16px 90px;
  }
`;

const Header = styled.header`
  position: relative;
  z-index: 2;
  max-width: 950px;
  margin: 0 auto 75px;
  text-align: center;

  .eyebrow {
    display: inline-block;
    margin-bottom: 22px;
    padding: 9px 19px;
    border: 1px solid rgba(96,165,250,0.25);
    border-radius: 30px;
    background: rgba(37,99,235,0.10);
    color: #93c5fd;
    font-size: 14px;
    font-weight: 700;
  }

  h1 {
    margin: 0 0 24px;
    color: #fff;
    font-size: clamp(43px, 5vw, 66px);
    line-height: 1.12;
    font-weight: 900;
    letter-spacing: -2px;
  }

  h1 span,
  .gradient {
    background: linear-gradient(90deg, #60a5fa, #818cf8, #c084fc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  p {
    max-width: 760px;
    margin: auto;
    color: #b8c6d9;
    font-size: 19px;
    line-height: 1.9;
  }

  @media (max-width: 600px) {
    margin-bottom: 55px;

    h1 {
      font-size: 39px;
      letter-spacing: -1px;
    }

    p {
      font-size: 16px;
    }
  }
`;

const AboutText = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1050px;
  margin: 0 auto 75px;
  text-align: center;
  color: white;

  h2 {
    margin: 0 0 17px;
    color: #fff;
    font-size: clamp(30px, 4vw, 45px);
    line-height: 1.25;
    font-weight: 900;
    letter-spacing: -1px;
  }

  h2 span {
    background: linear-gradient(90deg, #60a5fa, #818cf8, #c084fc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .intro {
    max-width: 900px;
    margin: 0 auto 19px;
    color: #cbd5e1;
    font-size: 18px;
    line-height: 2;
  }

  .highlight {
    max-width: 900px;
    margin: 35px auto 0;
    padding: 27px 30px;
    border-radius: 20px;
    border: 1px solid rgba(96,165,250,0.22);
    background: linear-gradient(120deg, rgba(37,99,235,0.13), rgba(124,58,237,0.10));
    color: #fff;
    font-size: 21px;
    font-weight: 800;
    line-height: 1.8;
  }

  @media (max-width: 600px) {
    margin-bottom: 55px;

    h2 {
      font-size: 30px;
    }

    .intro {
      font-size: 16px;
      line-height: 1.85;
    }

    .highlight {
      padding: 22px 17px;
      font-size: 18px;
    }
  }
`;

const Features = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto 90px;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 22px;

  .feature {
    position: relative;
    min-height: 235px;
    padding: 32px 25px;
    text-align: center;
    border-radius: 24px;

    background: linear-gradient(
      145deg,
      rgba(255,255,255,0.095),
      rgba(255,255,255,0.035)
    );

    border: 1px solid rgba(255,255,255,0.10);
    box-shadow: 0 18px 50px rgba(0,0,0,0.22);
    backdrop-filter: blur(18px);

    transition: transform 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease;
  }

  .feature:hover {
    transform: translateY(-7px);
    border-color: rgba(96,165,250,0.35);
    box-shadow: 0 25px 60px rgba(0,0,0,0.35), 0 10px 35px rgba(37,99,235,0.12);
  }

  .icon {
    width: 65px;
    height: 65px;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 18px;
    background: rgba(59,130,246,0.10);
    border: 1px solid rgba(96,165,250,0.16);
    font-size: 30px;
  }

  h3 {
    margin: 0 0 12px;
    color: #fff;
    font-size: 20px;
    font-weight: 800;
  }

  p {
    margin: 0;
    color: #aebbd0;
    font-size: 15px;
    line-height: 1.8;
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;

    .feature {
      min-height: auto;
      padding: 28px 22px;
    }
  }
`;

const MarketingSection = styled.section`
  position: relative;
  z-index: 2;
  max-width: 1150px;
  margin: 0 auto 80px;
  padding: 55px 50px;
  border-radius: 32px;

  background: linear-gradient(
    145deg,
    rgba(37,99,235,0.12),
    rgba(124,58,237,0.08),
    rgba(255,255,255,0.035)
  );

  border: 1px solid rgba(255,255,255,0.10);
  box-shadow: 0 30px 80px rgba(0,0,0,0.30);

  h2 {
    margin: 0 0 25px;
    color: white;
    font-size: clamp(28px, 3vw, 38px);
    font-weight: 900;
    line-height: 1.35;
    text-align: center;
  }

  h2 span {
    background: linear-gradient(90deg, #60a5fa, #c084fc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    max-width: 950px;
    margin: 0 auto 18px;
    color: #cbd5e1;
    font-size: 17px;
    line-height: 2;
    text-align: center;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 700px) {
    padding: 35px 22px;
    border-radius: 24px;

    h2 {
      font-size: 28px;
    }

    p {
      font-size: 16px;
      line-height: 1.85;
    }
  }
`;

const ProcessSection = styled.section`
  position: relative;
  z-index: 2;
  max-width: 1150px;
  margin: 0 auto 90px;
  text-align: center;

  h2 {
    margin: 0 0 45px;
    color: white;
    font-size: clamp(30px, 4vw, 40px);
    font-weight: 900;
  }

  h2 span {
    background: linear-gradient(90deg, #60a5fa, #c084fc);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .steps {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
  }

  .step {
    padding: 29px 22px;
    border-radius: 22px;
    background: rgba(255,255,255,0.045);
    border: 1px solid rgba(255,255,255,0.08);
    transition: transform 0.3s ease, border-color 0.3s ease;
  }

  .step:hover {
    transform: translateY(-5px);
    border-color: rgba(96,165,250,0.3);
  }

  .number {
    width: 48px;
    height: 48px;
    margin: 0 auto 17px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    color: white;
    background: linear-gradient(135deg, #2563eb, #6366f1);
    font-weight: 900;
  }

  h3 {
    margin: 0 0 10px;
    color: white;
    font-size: 19px;
  }

  p {
    margin: 0;
    color: #aebbd0;
    font-size: 14px;
    line-height: 1.8;
  }
`;

const CTA = styled.section`
  position: relative;
  z-index: 2;
  max-width: 950px;
  margin: 0 auto;
  padding: 52px 30px;
  text-align: center;
  border-radius: 30px;

  background: linear-gradient(
    135deg,
    rgba(37,99,235,0.16),
    rgba(124,58,237,0.13)
  );

  border: 1px solid rgba(96,165,250,0.20);

  h2 {
    margin: 0 0 17px;
    color: white;
    font-size: clamp(28px, 3vw, 37px);
    font-weight: 900;
    line-height: 1.35;
  }

  p {
    max-width: 750px;
    margin: 0 auto;
    color: #cbd5e1;
    font-size: 17px;
    line-height: 1.9;
  }

  @media (max-width: 600px) {
    padding: 38px 20px;

    h2 {
      font-size: 28px;
    }

    p {
      font-size: 15px;
    }
  }
`;

class About extends Component {
  render() {
    return (
      <Page>
        <Header>

          <h1>
            אנחנו בונים אתרים שמביאים <span>לקוחות</span>
          </h1>

          <p>
            בניית אתרים מקצועיים לעסקים, עם שילוב של עיצוב,
            טכנולוגיה וחשיבה שיווקית. המטרה שלנו היא להפוך
            את הנוכחות הדיגיטלית שלכם לכלי שמייצר הזדמנויות עסקיות.
          </p>
        </Header>

        <AboutText>
          <h2>
            נעים להכיר, אנחנו <span>SITE B2B</span>
          </h2>

          <h2>
            לא רק אתר יפה, אתר עם <span>מטרה עסקית</span>
          </h2>

          <p className="intro">
            אנחנו בחברת SITE B2B מאמינים שאתר אינטרנט צריך לעשות
            הרבה יותר מאשר להיראות טוב. הוא צריך להציג את העסק
            בצורה מקצועית, להעביר את המסר הנכון, לבנות אמון
            ולעזור להפוך גולשים ללקוחות פוטנציאליים.
          </p>

          <p className="intro">
            לכן אנחנו לא בונים אתרים רק ליופי. אנחנו מתכננים ומפתחים
            אתרים לעסקים מתוך חשיבה על הלקוחות שלכם: מה הם מחפשים,
            מה חשוב להם לדעת ואיך להוביל אותם בצורה ברורה ונוחה
            ליצירת קשר עם העסק.
          </p>

          <p className="intro">
            כל אתר נבנה בהתאם לתחום הפעילות, לקהל היעד ולמטרות
            העסקיות שלכם, תוך הקפדה על עיצוב מודרני, חוויית משתמש,
            התאמה מלאה למובייל ותשתית טכנית נכונה.
          </p>

          <div className="highlight">
            ב־SITE B2B לא בונים אתרים רק כדי שיהיו יפים —
            בונים אתרים כדי לעזור לעסקים לבלוט, להגיע ללקוחות
            חדשים ולייצר יותר הזדמנויות עסקיות
          </div>
        </AboutText>

        <Features>
          <div className="feature">
            <div className="icon">🎯</div>
            <h3>אתרים שמכוונים ללקוחות</h3>
            <p>
              תכנון האתר מתמקד במסר ברור, בהצגת השירותים
              ובהנעה לפעולה כדי לעודד לקוחות פוטנציאליים ליצור קשר
            </p>
          </div>

          <div className="feature">
            <div className="icon">🔍</div>
            <h3>תשתית לקידום אורגני בגוגל</h3>
            <p>
              מבנה עמודים נכון, היררכיית כותרות, תוכן איכותי
              והגדרות SEO בסיסיות שמסייעות למנועי החיפוש להבין את האתר
            </p>
          </div>

          <div className="feature">
            <div className="icon">📱</div>
            <h3>התאמה מלאה למובייל</h3>
            <p>
              אתר רספונסיבי המותאם למחשבים, טאבלטים וסמארטפונים,
              עם חוויית גלישה נוחה בכל גודל מסך
            </p>
          </div>

          <div className="feature">
            <div className="icon">⚡</div>
            <h3>מהירות וביצועים</h3>
            <p>
              דגש על טעינה יעילה, קוד מסודר וחוויית משתמש
              מהירה ונוחה, בהתאם למאפייני האתר
            </p>
          </div>

          <div className="feature">
            <div className="icon">💼</div>
            <h3>פתרון שמתאים לעסק שלכם</h3>
            <p>
              בניית אתר תדמית או אתר עסקי בהתאם לשירותים,
              לקהל היעד, לאופי המותג ולמטרות העסק
            </p>
          </div>

          <div className="feature">
            <div className="icon">💬</div>
            <h3>יצירת קשר פשוטה</h3>
            <p>
              שילוב אפשרויות פנייה נוחות כמו טלפון, WhatsApp
              וטפסי יצירת קשר כדי להקל על הלקוחות לפנות אליכם
            </p>
          </div>
        </Features>

        <MarketingSection>
          <h2>
            אתר יפה זה רק ההתחלה — <span>התוצאה העסקית חשובה יותר</span>
          </h2>

          <p>
            אתר אינטרנט הוא אחד מנכסי השיווק החשובים של העסק.
            הוא מאפשר ללקוחות להכיר אתכם, להבין אילו שירותים
            אתם מציעים, להתרשם מהניסיון שלכם ולבדוק כיצד תוכלו
            לעזור להם.
          </p>

          <p>
            אבל עיצוב לבדו לא מספיק. אתר עסקי צריך להיות מתוכנן
            כך שהמבקר יבין במהירות מה העסק מציע, מדוע כדאי לו
            לבחור דווקא בכם ומה עליו לעשות כדי לקבל פרטים נוספים.
          </p>

          <p>
            ב־SITE B2B אנחנו משלבים חשיבה שיווקית כבר בתהליך
            בניית האתר: תוכן ממוקד, כותרות ברורות, הצגת יתרונות,
            מבנה נוח והנעה לפעולה במקומות הנכונים.
          </p>

          <p>
            כך האתר לא משמש רק ככרטיס ביקור דיגיטלי, אלא ככלי
            שיכול לתמוך בפעילות השיווקית של העסק ולעזור ביצירת
            פניות והזדמנויות חדשות.
          </p>
        </MarketingSection>

        <MarketingSection>
          <h2>
             בניית אתרים עם בסיס נכון ל ־ <span>לקידום בגוגל </span>
             <span>מבלי לשלם</span>
          </h2>

          <p>
            כאשר בונים אתר לעסק, חשוב לחשוב גם על הדרך שבה
            לקוחות יוכלו למצוא אותו במנועי החיפוש. קידום אורגני
            בגוגל הוא תהליך מתמשך, ולכן חשוב להתחיל עם תשתית
            טכנית ותוכנית תוכן מסודרת.
          </p>

          <p>
            בתהליך בניית האתר אנחנו שמים דגש על היררכיית כותרות,
            מבנה עמודים ברור, כתובות URL מסודרות, תוכן רלוונטי,
            התאמה למכשירים ניידים, מטא־תגים וביצועים.
          </p>

          <p>
            בנוסף, חשוב ליצור תוכן שנותן מענה אמיתי לשאלות
            ולצרכים של קהל היעד. תוכן איכותי וממוקד מסייע
            למבקרים להבין את השירותים שלכם ולמנועי החיפוש
            להבין במה עוסק כל עמוד.
          </p>

          <p>
            חשוב לדעת: תשתית SEO נכונה היא נקודת פתיחה חשובה,
            אך קידום בגוגל דורש גם עבודה שוטפת, תוכן איכותי,
            אופטימיזציה ומעקב לאורך זמן.
          </p>
        </MarketingSection>

        <ProcessSection>
          <h2>
            איך אנחנו בונים את <span>האתר שלכם?</span>
          </h2>

          <div className="steps">
            <div className="step">
              <div className="number">01</div>
              <h3>היכרות עם העסק</h3>
              <p>
                לומדים על תחום הפעילות, השירותים, קהל היעד
                והמטרות העסקיות שלכם
              </p>
            </div>

            <div className="step">
              <div className="number">02</div>
              <h3>תכנון ואפיון</h3>
              <p>
                מתכננים את מבנה האתר, העמודים והתוכן
                בהתאם לצרכים של העסק והלקוחות
              </p>
            </div>

            <div className="step">
              <div className="number">03</div>
              <h3>עיצוב ופיתוח</h3>
              <p>
                מעצבים ומפתחים אתר מקצועי, מודרני,
                רספונסיבי ונוח לשימוש
              </p>
            </div>

            <div className="step">
              <div className="number">04</div>
              <h3>הכנה למנועי חיפוש</h3>
              <p>
                מטמיעים מבנה ותשתית SEO בסיסית
                ומכינים את האתר להמשך עבודה וקידום
              </p>
            </div>

            <div className="step">
              <div className="number">05</div>
              <h3>השקה והמשך הדרך</h3>
              <p>
                מעלים את האתר לאוויר ומאפשרים לעסק
                להתחיל לבסס נוכחות דיגיטלית מקצועית
              </p>
            </div>
          </div>
        </ProcessSection>

        <CTA>
          <h2>
            הגיע הזמן שהאתר שלכם יעבוד בשביל העסק
          </h2>

          <p>
            רוצים אתר תדמית מקצועי או אתר עסקי שיציג אתכם
            בצורה הטובה ביותר ויעזור ללקוחות פוטנציאליים
            להגיע אליכם? ב־SITE B2B בונים אתרים עם חשיבה
            עסקית, עיצוב מוקפד ומטרה ברורה: להפוך את הנוכחות
            שלכם באינטרנט לרווחית .
          </p>
        </CTA>
      </Page>
    );
  }
}

export default About;
